import { FC, useEffect, useState } from 'react';
import { Route } from 'react-router';
import { createUseStyles } from 'react-jss';
import dayjs from 'dayjs';
import { IonButton, IonButtons, IonContent, IonHeader, IonTitle, IonToolbar } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { ActiveCareMapProviderV2 } from '../ActiveCareMapProvider';
import { InnerhivePage } from '../InnerhivePage';
import { CareMapState } from '../caremap/CareMapState';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { QueryParamDrivenDrawerHelper } from '../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { ArrowLeftIcon, ChevronRightIcon, CloseIcon } from '../theme/Icons';
import { Text } from '../components/Text';
import { InlineLoadError } from '../components/InlineLoadError';
import { CaremapInsightsModelFactory } from './CaremapInsightsModelFactory';
import { InsightsModel } from './InsightsModel';
import { IInsight } from './IInsight';
import { InsightListCard } from './InsightListCard';
import { useInsightEnterAnimation } from './useInsightEnterAnimation';
import { insightTypeMeta } from './insightTypeMeta';
import { notUsefulReasonLabel } from './notUsefulReasons';
import { useAsyncEffect } from '../support/useAsyncEffect';

// The Insights page has no bottom-nav tab; it is reached only from the home insights section. Until a
// future "Bea" tab exists (blocked on Community retirement), the header X exits explicitly to home.
const HOME_PATH = '/caregiver';

// Feedback (thumbs) is offered on the most recent insights only; older ones are read-only in the list.
const FEEDBACK_LIMIT = 3;

export const InsightsPageRouteHandler: FC = () => (
    <Route exact path="/insights/:caremapId" render={routeParams => (
        <InsightsPage careMapId={routeParams.match.params.caremapId} />
    )} />
);

export const InsightsPage: FC<{ careMapId: string }> = ({ careMapId }) => (
    <ActiveCareMapProviderV2 careMapId={careMapId}>
        <ActiveInsightsPage />
    </ActiveCareMapProviderV2>
);

const useStyles = createUseStyles({
    toolbar: {
        // partially transparent so content reads through as it scrolls under the fixed header
        '--background': 'rgba(255, 255, 255, 0.85)',
    },
    content: {
        // keep the reading column comfortable on wide screens, matching ResourceDetailsPage
        maxWidth: 680,
        justifySelf: 'center',
        '--padding-start': 'var(--ion-padding, 16px)',
        '--padding-end': 'var(--ion-padding, 16px)',
        // clear the bottom tab bar (which overlays scroll content) plus device safe area, so the
        // last element -- the "not useful" archive link -- isn't hidden behind it. Needs !important
        // for the same specificity reason as --padding-top below.
        '--padding-bottom': 'calc(80px + var(--ion-safe-area-bottom)) !important',
        // skipAddingIonContent means IonContent isn't told the fixed IonHeader's height, so pad past it
        '--padding-top': 'calc(56px + var(--ion-safe-area-top)) !important',
    },
    intro: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '4px 0',
    },
    introAvatar: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        objectFit: 'cover' as const,
        flexShrink: 0,
    },
    dateHeader: {
        margin: '20px 2px 10px',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase' as const,
        color: 'var(--ion-color-step-500)',
    },
    cardWrap: {
        marginBottom: 12,
    },
    empty: {
        padding: '16px 2px',
    },
    // The Not-useful archive rows mirror the readable insight card styling for consistency.
    archiveList: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 12,
        marginTop: 20,
    },
    archiveRow: {
        background: 'var(--ion-background-color)',
        border: '1px solid var(--ion-color-step-150)',
        borderRadius: 14,
        padding: 14,
        cursor: 'pointer',
    },
    archiveType: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--ion-color-step-600)',
        marginBottom: 6,
    },
    archiveTitleRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 8,
        marginBottom: 8,
    },
    archiveTitleText: {
        flex: 1,
        minWidth: 0,
    },
    archiveReason: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 12,
        color: 'var(--ion-color-step-600)',
        background: 'var(--ion-color-step-100)',
        borderRadius: 999,
        padding: '3px 10px',
    },
    archiveLink: {
        marginTop: 20,
        paddingTop: 16,
        borderTop: '1px solid var(--ion-color-step-100)',
        textAlign: 'center' as const,
    },
    archiveLinkButton: {
        background: 'none',
        border: 0,
        color: 'var(--ion-color-step-600)',
        fontSize: 13,
        textDecoration: 'underline',
        cursor: 'pointer',
    },
});

// Insights created in the current calendar month group under "This month"; everything else "Earlier".
const periodLabel = (createdAt: string): string => {
    return dayjs(createdAt).isSame(dayjs(), 'month') ? 'This month' : 'Earlier';
};

// The page exits via an explicit X (push to home) rather than a history-back arrow: opening/closing
// the insight drawer pushes ?drawer= history entries, so history.back() would walk back through them
// and reopen drawers, trapping the user on the page. The archive's back arrow is pure component
// state (showArchive), so it never touches history and stays safe.
const PageHeader: FC<{ title: string; toolbarClass: string; onClose: () => void; onBack?: () => void }> = ({ title, toolbarClass, onClose, onBack }) => (
    <IonHeader slot="fixed">
        <IonToolbar className={toolbarClass}>
            {onBack && (
                <IonButtons slot="start">
                    <IonButton data-type="back-button" fill="clear" shape="round" onClick={onBack}>
                        <ArrowLeftIcon color="dark" />
                    </IonButton>
                </IonButtons>
            )}
            <IonTitle>{title}</IonTitle>
            <IonButtons slot="end">
                <IonButton data-type="close-button" fill="clear" shape="round" onClick={onClose}>
                    <CloseIcon color="dark" />
                </IonButton>
            </IonButtons>
        </IonToolbar>
    </IonHeader>
);

const ActiveInsightsPage: FC = () => {
    const classes = useStyles();

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const factory = useInstance(CaremapInsightsModelFactory);
    useSubscription(() => factory);

    const history = useInstance(HistoryViewModel);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);

    const [showArchive, setShowArchive] = useState(false);

    let model: InsightsModel | undefined;
    try {
        model = factory.forActiveCaremap();
    } catch {
        // no active caremap yet
    }

    useEffect(() => {
        mixpanel.track('Insights Page Opened');
    }, [mixpanel]);

    useAsyncEffect(async () => {
        if (model && !model.hasLoaded) {
            await model.load();
        }
    }, [model]);

    if (!model) {
        return null;
    }

    const openDetail = (insight: IInsight) => {
        drawerHelper.open({ mode: 'view-insight', insightId: insight.insightId, surface: 'insights-page' });
    };

    const active = model.getActive();
    const dismissed = model.getDismissed();

    const closePage = () => {
        history.push(HOME_PATH);
    };

    return (
        <InnerhivePage skipAddingIonContent hideAppHeader>
            <IonContent className={classes.content} data-type="insights-page">
                <PageHeader
                    title={showArchive ? 'Not useful' : 'Insights'}
                    toolbarClass={classes.toolbar}
                    onClose={closePage}
                    onBack={showArchive ? () => setShowArchive(false) : undefined}
                />

                {showArchive
                    ? <NotUsefulView insights={dismissed} classes={classes} onOpen={openDetail} />
                    : <ActiveView insights={active} model={model} classes={classes} onOpen={openDetail} archivedCount={dismissed.length} onShowArchive={() => setShowArchive(true)} />
                }
            </IonContent>
        </InnerhivePage>
    );
};

interface IViewProps {
    insights: IInsight[];
    classes: ReturnType<typeof useStyles>;
    onOpen: (insight: IInsight) => void;
}

const ActiveView: FC<IViewProps & { model: InsightsModel; archivedCount: number; onShowArchive: () => void }> = ({ insights, model, classes, onOpen, archivedCount, onShowArchive }) => {
    const enterClassName = useInsightEnterAnimation(insights.map(insight => insight.insightId));

    if (model.loadFailed) {
        return (
            <>
                <Intro classes={classes} />
                <InlineLoadError
                    dataId="insights-load-error"
                    message="Couldn't load insights. Check your connection and try again."
                    onRetry={() => model.load()}
                />
            </>
        );
    }

    if (insights.length === 0) {
        return (
            <>
                <Intro classes={classes} />
                <div className={classes.empty}>
                    <Text type="detail" data-type="insights-empty">You’re all caught up. New insights will appear here as Bea notices them.</Text>
                </div>
                {archivedCount > 0 && <ArchiveLink classes={classes} count={archivedCount} onClick={onShowArchive} />}
            </>
        );
    }

    return (
        <>
            <Intro classes={classes} />
            {insights.map((insight, index) => {
                const period = periodLabel(insight.createdAt);
                const previousPeriod = index === 0 ? undefined : periodLabel(insights[index - 1].createdAt);
                const header = period !== previousPeriod ? period : undefined;
                return (
                    <div key={insight.insightId} className={enterClassName(insight.insightId)}>
                        {header && <div className={classes.dateHeader}>{header}</div>}
                        <div className={classes.cardWrap}>
                            <InsightListCard
                                insight={insight}
                                model={model}
                                showFeedback={index < FEEDBACK_LIMIT}
                                onOpen={() => onOpen(insight)}
                            />
                        </div>
                    </div>
                );
            })}
            {archivedCount > 0 && <ArchiveLink classes={classes} count={archivedCount} onClick={onShowArchive} />}
        </>
    );
};

const NotUsefulView: FC<IViewProps> = ({ insights, classes, onOpen }) => (
    <>
        <div className={classes.intro}>
            <img src="/assets/images/help/helper-bea.png" alt="Bea" className={classes.introAvatar} />
            <Text type="detail">Insights you told Bea weren’t useful, with the reason you gave.</Text>
        </div>
        <div className={classes.archiveList}>
            {insights.map(insight => {
                const { label, Icon } = insightTypeMeta(insight.type);
                const reason = notUsefulReasonLabel(insight.myFeedback?.note) || 'Not useful';
                return (
                    <div key={insight.insightId} className={classes.archiveRow} data-type="archived-insight" data-insight-id={insight.insightId} onClick={() => onOpen(insight)}>
                        <div className={classes.archiveType}><Icon color="medium" size="small" />{label}</div>
                        <div className={classes.archiveTitleRow}>
                            <span className={`${classes.archiveTitleText} mp-mask`}><Text type="subtitle" color="dark">{insight.title}</Text></span>
                            <ChevronRightIcon color="medium" size="small" />
                        </div>
                        <span className={classes.archiveReason}>&#128078; {reason}</span>
                    </div>
                );
            })}
        </div>
    </>
);

const Intro: FC<{ classes: ReturnType<typeof useStyles> }> = ({ classes }) => (
    <div className={classes.intro}>
        <img src="/assets/images/help/helper-bea.png" alt="Bea" className={classes.introAvatar} />
        <Text type="detail">Bea learns from every recording, journal entry, and note. When she spots something worth knowing, it shows up here.</Text>
    </div>
);

const ArchiveLink: FC<{ classes: ReturnType<typeof useStyles>; count: number; onClick: () => void }> = ({ classes, count, onClick }) => (
    <div className={classes.archiveLink}>
        <button className={classes.archiveLinkButton} onClick={onClick} data-id="insights-not-useful-link">
            Insights you marked not useful ({count})
        </button>
    </div>
);
